import React, { useEffect, useRef, useState, useMemo, useLayoutEffect } from "react";
import Select, { components } from 'react-select';
import TreeList, { MenuType } from "./../Widgets/TreeList";
import styles from "./TreeSelect.module.scss";
import _ from "lodash";
import classNames from "classnames";
import { Button, Span } from "./../../";
import { CloseIcon, SearchIcon } from "@netapp/bxp-style/react-icons/Action";
import { isElementVisible } from '../../utils/ElementVisibilityUtil';
import { DropdownArrowUpIcon, DropdownArrowDownIcon } from "@netapp/bxp-style/react-icons/Navigation";

const { SingleValue, MultiValue, ClearIndicator } = components;

// Default dropdown Arrow Icon, can be configurable
const DefaultDropdownArrowUpIcon = ({color}) => {
    return <DropdownArrowUpIcon color={color}/>
};
// Default dropdown Arrow Icon, can be configurable
const DefaultDropdownArrowDownIcon = ({color}) => {
    return <DropdownArrowDownIcon color={color} />
};

// Default Filtering is done on the top level of Tree view and compared only with labelKey,
// Consumers can pass this filtering according to their needs.
const DefaultFiltering = (data, filterKey, searchKey) => {
    if (!_.isUndefined(filterKey) && filterKey !== '') {
        const lowerCasedFilter = _.toLower(filterKey);
        return _.filter(data, option => {
            return (option[searchKey] && _.includes(_.toLower(option[searchKey]), lowerCasedFilter))
        })
    } else {
        return data;
    }
}

const getDefaultSelectedValues = (treeData, defaultSelectedValues, uniqueIdentifier, childKey) => {
    let defaultValues = [];
    if (defaultSelectedValues) {
        treeData.map((treeItem) => {
            if (_.findIndex(defaultSelectedValues, (selectedValue) => _.isEqual(selectedValue[uniqueIdentifier], treeItem[uniqueIdentifier])) > -1) {
                defaultValues.push(treeItem);
            }
            if (!(_.isUndefined(treeItem[childKey]) || _.isEmpty(treeItem[childKey]))) {
                defaultValues.push(...getDefaultSelectedValues(treeItem[childKey], defaultSelectedValues, uniqueIdentifier, childKey));
            }
        });
    }
    return defaultValues
}

// Styles to be set for Dropdown Menu, Single/Multi value Rendering.
const contentTypeSelectorStyles = {
    singleValue: () => ({
        fontSize: '14px',
        color: 'var(--text-primary)',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        padding: '0px 15px'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: 'var(--button-primary-bg)',
        color: 'white',
        padding: '2px 5px',
        margin: '5px 3px'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: 'white',
        fontSize: '13.5px',
        margin: '2px 0px',
        padding: '0px'
    }),
    multiValueRemove: () => ({
        display: 'none'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    valueContainer: (base) => ({
        ...base,
        display: "flex",
        padding: 0,
        'flex-wrap': 'unset',
        '-webkit-flex-wrap': 'unset'
    }),
    control: (base) => ({
        ...base,
        minHeight: '40px',
        padding: '0px 5px',
        backgroundColor: 'var(--background-content)'
    }),
    placeholder: (base) => ({
        ...base,
        color: 'var(--text-disabled)',
        fontSize: '14px',
        padding: '0 10px',
    }),
    menu: (base) => ({
        ...base,
        minWidth: '350px',
        backgroundColor: 'var(--background-content)',
        zIndex: 3000000000
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 300000000
    })
};

// Custom Menu list dropdown down to render the tree grid along with search input field and accept/cancel button
export const CustomMenuList = (menuListProps) => {
    const { selectProps,
        setValue } = menuListProps;
    const { onInputChange,
        inputValue,
        onMenuInputFocus,
        treeListParams } = selectProps;
    const { treeData,
        labelKey,
        isSearchable,
        childKey,
        menuType,
        defaultSelectedValues,
        selectedValues,
        dropdownUpIcon,
        dropdownDownIcon,
        isOptionDisabled,
        onSelectionChange,
        isRequireAcceptOnChange,
        acceptChanges,
        searchPlaceholder,
        isFirst,
        customFilter,
        noOptionsMessage,
        uniqueIdentifier,
        iconRenderer,
        expandAll,
        bottomBorder,
        customStyles,
     } = treeListParams;

    // This state is to manage the final selected values. In case of multi selection, if user selects
    // multiple choices and clicks on cancel, it should updated the last accepted value. 
    const [lastSelectedValues, setLastSelectedValues] = useState([]);
    const [isAcceptDisable, setisAcceptDisable] = useState(true);
    const menuContainerRef = useRef(null);
    let menuTreeListRef = useRef(null)
    // The filter value user had searched for
    const [filter, setFilter] = useState();
    // Filtering function
    const filteredOptions = useMemo(() => customFilter(treeData, filter, labelKey), [filter]);
    let maxOptionsCount = 6; // Max number of options to show in the dropdown menu
    let isDownwardOptionsComplete = false; // Flag to inform that all the options for opening downward is complete
    let isUpwardOptionsComplete = false; // Flag to inform that all the options for opening upward is complete
    let isMenuVisible = false; // Flag to inform dropdown Menu option is visible
    const ariaAttributes = {
        "aria-autocomplete": "list",
        "aria-label": selectProps["aria-label"],
        "aria-labelledby": selectProps["aria-labelledby"]
    };

    // This method is called with accept or cancel button is clicked.
    const onAcceptOrCancel = (isAccept) => {
        setValue(isAccept ? [...lastSelectedValues] : [...selectedValues]);
        onSelectionChange(isAccept ? [...lastSelectedValues] : [...selectedValues]);
        acceptChanges();
    }
    // This method is called when any change in the items slection.
    const onOptionSelected = (selectedValues) => {
        setLastSelectedValues([...selectedValues]);
        if (!(isRequireAcceptOnChange) || (isFirst && defaultSelectedValues)) {
            setValue([...selectedValues]);
            onSelectionChange([...selectedValues]);
        }
        if (!isFirst) setisAcceptDisable(false);
    }

    /**
     * Function to modify the styles to show dropdown menu in the view port based on the number of options it can fit in viewport
     * @param {*} isUpward - flag to decide which direction it is checking
     * @param {*} maxOptions - max number of menu options to be shown, by default it is 6
     */
    const modifyStyles = ( isUpward, maxOptions = 6 ) => {
        maxOptionsCount = maxOptions;
        while(!isMenuVisible && maxOptionsCount > 1) {
            // This is the max height that needs to be set for menu dropdown
            let menuMaxHeight = 48 * maxOptionsCount;

            if (menuContainerRef?.current?.children) {
                menuTreeListRef.current.style.maxHeight = menuMaxHeight + 'px';
            }
            // Check Menu is visible for maxOptionsCount, if not visible reduce the maxOptionsCount by 1 and test again
            isMenuVisible = isElementVisible(menuContainerRef?.current);
            maxOptionsCount--;
            // If maxOptionsCount is set to minimum that is 2, and still Menu is not visible then update the flag that all options are complete
            if (maxOptionsCount == 1) {
               if (isUpward) {
                    isUpwardOptionsComplete = true;
               } else {
                    isDownwardOptionsComplete = true;
               }
            }      
        }
    }

    const resetStyles = () => {
        isDownwardOptionsComplete = false;
        isUpwardOptionsComplete = false;
        isMenuVisible = false;
        if (menuContainerRef?.current?.parentElement) {
            menuContainerRef.current.parentElement.style.bottom = '';
            menuContainerRef.current.parentElement.style.top = '100%';
        }
    }

    useLayoutEffect(
        () => {
            if (!menuContainerRef?.current) return;
            const resize = () => {
                resetStyles();
                isMenuVisible = isElementVisible(menuContainerRef?.current);

                if (!isMenuVisible) {
                    modifyStyles(false);
                }
                // If to check upward options are available or not
                if (!isMenuVisible && isDownwardOptionsComplete ) {
                    const bottomPosition = '40px'
                    menuContainerRef.current.parentElement.style.bottom = bottomPosition;
                    menuContainerRef.current.parentElement.style.top = 'unset';
                    modifyStyles(true);
                }
                // If to both downward and upward options are not availble then fall back to showing 2 options downwards
                if (!isMenuVisible && isDownwardOptionsComplete & isUpwardOptionsComplete) {
                    resetStyles();
                    modifyStyles(false, 2);
                }
            }

            setTimeout(resize, 1000)
            menuTreeListRef?.current?.addEventListener("click", resize);
            return () => {
                menuTreeListRef?.current?.removeEventListener("click", resize);
            };
        },
        [menuContainerRef, filteredOptions, menuTreeListRef],
    );

    // Search Widget is not used because the search input field is becoming non clickable inside the select dropdown due to auto focus option
    // So an input field is customized to show inside the dropdown menu.
    // Search Widget works fine outside of the dropdown menu.
    return (<div ref={menuContainerRef} style={{padding: '5px 0px'}}>
        {isSearchable && <div className={classNames(styles['tree-select-menu-base'])} style={{...(customStyles?.treeSelectMenuBase ?? {})}}>
            <div className={classNames(styles['search-widget'], styles['open'])} style={{...(customStyles?.treeSelectSearchWidgetBase ?? {})}}>
                <Button variant="icon" className={classNames(styles['open-button'], styles['always-open'])} tabIndex={-1}><SearchIcon /></Button>
                {/* This is Search Input Field, placeholder is configurable */}
                <input
                    className={classNames(styles['search-input-field'])}
                    style={{...(customStyles?.searchInputField ?? {})}}
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck="false"
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                        setFilter(e.currentTarget.value);
                        onInputChange(e.currentTarget.value, {
                            action: "input-change"
                        })
                    }
                    }
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.target.focus();
                    }}
                    onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.target.focus();
                    }}
                    onFocus={onMenuInputFocus}
                    placeholder={searchPlaceholder}
                    {...ariaAttributes}
                />
                <button type="button"
                    className={classNames(styles['close-button'], { [styles["show-close-button"]]: filter?.length > 0 })}
                    onClick={(e) => {
                        setFilter('');
                        onInputChange('', {
                            action: "input-change"
                        })
                    }} aria-label="Clear search"><CloseIcon /></button>
            </div>
        </div>}
        {filteredOptions && filteredOptions?.length > 0 && <TreeList treeData={filteredOptions}
            ref={menuTreeListRef}
            childKey={childKey}
            menuType={menuType}
            selectedValues={selectedValues ? [...selectedValues] : []}
            onChange={onOptionSelected}
            dropdownUpIcon={dropdownUpIcon}
            dropdownDownIcon={dropdownDownIcon}
            isOptionDisabled={isOptionDisabled}
            labelKey={labelKey}
            uniqueIdentifier={uniqueIdentifier}
            iconRenderer={iconRenderer}
            expandLevel={expandAll}
            bottomBorder={bottomBorder}
            customStyles={customStyles}
        />}
        {!filteredOptions?.length && <div className={styles["no-options"]} style={{...(customStyles?.noOptions ?? {})}}>
            <Span bold>{noOptionsMessage ? noOptionsMessage() : "No Options"}</Span>
        </div>}
        {isRequireAcceptOnChange && (filteredOptions?.length > 0) && <div className={styles["menu-actions"]} style={{...(customStyles?.menuActions ?? {})}} >
            <button disabled={isAcceptDisable}
                onClick={() => {
                    onAcceptOrCancel(true);
            }}>Accept
            </button>
            <button onClick={() => onAcceptOrCancel(false)}>Cancel</button>
        </div>}
    </div>
    );
};

const CustomSingleValue = ({ children, selectProps, ...props }) => {
    const { treeListParams } = selectProps;
    const { labelKey } = treeListParams;

    return (
        
        <SingleValue {...props}>
            <Span level="16"
                ellipsis={true}
                title={_.get(props.getValue()[0], labelKey)}>
                    {_.get(props.getValue()[0], labelKey)}
            </Span>
        </SingleValue>
    )
}

const CustomMultiValue = ({ children, selectProps, ...props }) => {
    const { treeListParams } = selectProps;
    const { labelKey } = treeListParams;
    const { index } = props;

    return (
        <MultiValue {...props}>{_.get(props.getValue()[index], labelKey)}</MultiValue>
    )
}

// This is customized component for close icon inside select input field
const CustomClearIndicator = (props) => {
    const { clearValue, selectProps, innerProps } = props;
    const { treeListParams } = selectProps;
    const { onSelectionChange } = treeListParams;
    // This method will be called to clear all the selected elements at once in case of multiple selection
    const clearAll = () => {
        onSelectionChange([]);
        clearValue();
    }

    const customInnerProps = {
        ...innerProps,
        onMouseDown: clearAll,
        onTouchEnd: clearAll
    };
    return (
        <ClearIndicator {...props} innerProps={customInnerProps}></ClearIndicator>
    );
};

const CustomDropdownIndicator = (
    props
  ) => {
    const { selectProps } = props;
    return (
        <div >
            {(!selectProps?.menuIsOpen) && <DefaultDropdownArrowDownIcon color={selectProps?.isDisabled ? "disabled" : "text"} />}
            {(selectProps?.menuIsOpen) && <DefaultDropdownArrowUpIcon color={selectProps?.isDisabled ? "disabled" : "text"}/>}
        </div>
    );
};

const CustomComparator = (value1, value2, uniqueIdentifier) => {
    const difference1 = _.differenceBy(value1, value2, uniqueIdentifier);
    const difference2 = _.differenceBy(value2, value1, uniqueIdentifier);
    return difference1?.length === 0 && difference2?.length === 0
}

const TreeSelect = React.memo(({
    treeData,
    childKey = 'childrens',
    labelKey = 'label',
    dropdownUpIcon,
    dropdownDownIcon,
    menuType = MenuType.SINGLE,
    defaultSelectedValues = [],
    isOptionDisabled,
    onChange = _.noop,
    isOpened = false,
    onOpenOrClose = _.noop,
    customSelectorStyles,
    inlineSelect = false,
    isSearchable = false,
    isRequireAcceptOnChange = false,
    searchPlaceholder = 'Search...',
    selectPlaceholder = "Select...",
    customFilter = DefaultFiltering,
    noOptionsMessage,
    menuPortalTarget,
    isSelectDisabled,
    uniqueIdentifier,
    iconRenderer,
    name,
    expandLevel,
    bottomBorder,
    customStyles,
    value
}) => {
    const isMulti = _.toLower(menuType) === _.toLower(MenuType.MULTI);
    const [isMenuOpen, setIsMenuOpen] = useState(isOpened);
    const [inputValue, setInputValue] = useState("");
    const [isFirst, setIsFirst] = useState(true);
    const [selectedValues, setSelectedValues] = useState(getDefaultSelectedValues(treeData, defaultSelectedValues, uniqueIdentifier, childKey));
    const selectInputRef = useRef();
    // method called with selection changes in child component.
    const onSelectionChange = (selectedVals) => {
        setSelectedValues([...selectedVals]);
    }

    useEffect(() => {
        onOpenOrClose(isMenuOpen);
    }, [isMenuOpen]);

    useEffect(() => {
        if (!CustomComparator(selectedValues, value, uniqueIdentifier) && value) {
            setSelectedValues(getDefaultSelectedValues(treeData, value, uniqueIdentifier, childKey))
            selectInputRef?.current?.select?.setValue([...getDefaultSelectedValues(treeData, value, uniqueIdentifier, childKey)])
        }
    }, [value])

    // useEffect hook to pass the selected values to parent component when selectedValues changes.
    useEffect(() => {
        if (!isFirst) {
            onChange([...selectedValues], {name});
        }
        if (!(isRequireAcceptOnChange) && !isFirst && !isMulti) {
            setIsMenuOpen(false);
        }
        if (isFirst) {
            setIsFirst(false)
        }
    }, [selectedValues])

    const expandAll = useMemo(() => {
        if(inputValue && inputValue !== '') {
            return 'max'
        } else {
            return expandLevel
        }
    }, [inputValue]);

    // Change the focus to select field when Items are selected.
    const acceptChanges = () => {
        setIsMenuOpen(false);
    }

    // These are styles are to customize the select component
    let selectStyles = {
        ...contentTypeSelectorStyles,
    }

    // If it is inline set few css properties to container and control to make it look inline.
    if (inlineSelect) {
        const singleValueStyles = {...selectStyles?.singleValue()}
        const placeholder = {...selectStyles?.placeholder()}
        selectStyles = {
            ...selectStyles,
            container: (provided) => ({
                ...provided,
                borderBottom: '1px solid var(--field-border)',
                ...(customStyles?.container ?? {})
            }),
            control: (provided) => ({
                ...provided,
                cursor: 'pointer',
                backgroundColor: 'unset',
                border: '0',
                boxShadow: 'unset',
                cursor: 'pointer',
                height: '40px'
            }),
            singleValue: () => ({
                ...singleValueStyles,
                ...(customStyles?.singleValue ?? {})
            }),
            placeholder: () => ({
                ...placeholder,
                ...(customStyles?.placeholder ?? {})
            })
        }
    }

    return (
        <div style={{...(customStyles?.treeSelectBase ?? {})}}>
            <Select
                ref={selectInputRef}
                defaultValue={[...getDefaultSelectedValues(treeData, defaultSelectedValues, uniqueIdentifier, childKey)]}
                styles={{ ...selectStyles, ...customSelectorStyles }}
                classNamePrefix="select"
                components={{
                    MenuList: CustomMenuList,
                    SingleValue: CustomSingleValue,
                    MultiValue: CustomMultiValue,
                    ClearIndicator: CustomClearIndicator,
                    DropdownIndicator: CustomDropdownIndicator
                }}
                inputValue={inputValue}
                isSearchable={false}
                isMulti={isMulti}
                isDisabled={isSelectDisabled}
                onMenuInputFocus={() => {
                    setIsMenuOpen(true)
                }}
                menuPortalTarget={menuPortalTarget ? menuPortalTarget : null}
                onInputChange={(val) => { setInputValue(val) }}
                menuIsOpen={isMenuOpen}
                onMenuOpen={() => {
                    setIsMenuOpen(true);
                }}
                onBlur={() => {
                    setIsMenuOpen(false);
                }}
                isFocused={isMenuOpen}
                autoFocus={isOpened}
                openMenuOnClick={true}
                placeholder={selectPlaceholder}
                menuPosition={'absolute'}
                backspaceRemovesValue={false}
                menuShouldScrollIntoView={false}    
                treeListParams={
                    {
                        treeData,
                        childKey,
                        labelKey,
                        dropdownUpIcon,
                        dropdownDownIcon,
                        menuType,
                        selectedValues,
                        defaultSelectedValues,
                        isOptionDisabled,
                        onSelectionChange,
                        customSelectorStyles,
                        inlineSelect,
                        isSearchable,
                        isRequireAcceptOnChange,
                        acceptChanges,
                        searchPlaceholder,
                        isFirst,
                        customFilter,
                        noOptionsMessage,
                        uniqueIdentifier,
                        iconRenderer,
                        expandAll,
                        bottomBorder,
                        customStyles,
                    }
                }
            />
        </div>
    );
})

export default TreeSelect
